import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";

export default function BuyerLifestyle() {
  const saved = useStore().state.buyer.current.preferences.lifestyle || {};
  const [form, setForm] = useState({
    pets: saved.pets || [],
    entertainment: saved.entertainment || [],
    quietSpaces: saved.quietSpaces || [],
    smartHome: saved.smartHome || [],
    energy: saved.energy || [],
    maintenance: saved.maintenance || "",
    specificNeeds: saved.specificNeeds || [],
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <ChecklistStep
      title="Lifestyle"
      lead="How you actually live — pets, gathering, quiet, technology, efficiency, and upkeep."
      preferenceKey="lifestyle"
      value={form}
    >
      <section className="checklist-section">
        <h2>Pets & Gathering</h2>
        <CheckboxGroup
          label="Pet-friendly features"
          name="pets"
          values={form.pets}
          onChange={update}
          layout="grid"
          options={[
            { value: "none", label: "No pets" },
            { value: "fenced-yard", label: "Fenced yard" },
            { value: "dog-wash", label: "Dog wash / mudroom" },
            { value: "hard-floors", label: "Hard-surface floors" },
            { value: "nearby-parks", label: "Near parks / trails" },
            { value: "hoa-pets-ok", label: "HOA must allow pets" },
          ]}
        />
        <CheckboxGroup
          label="Entertainment spaces"
          name="entertainment"
          values={form.entertainment}
          onChange={update}
          layout="grid"
          options={[
            { value: "open-kitchen", label: "Kitchen that hosts well" },
            { value: "dining", label: "Formal or large dining" },
            { value: "patio", label: "Indoor-outdoor flow" },
            { value: "bar", label: "Wet bar / butler pantry" },
            { value: "pool-party", label: "Space for outdoor gatherings" },
            { value: "media", label: "Movie / game night room" },
          ]}
        />
        <CheckboxGroup
          label="Quiet spaces"
          name="quietSpaces"
          values={form.quietSpaces}
          onChange={update}
          layout="grid"
          options={[
            { value: "office", label: "Dedicated office / Zoom door" },
            { value: "reading", label: "Reading nook" },
            { value: "away-street", label: "Bedrooms away from the street" },
            { value: "separate-living", label: "Second living area" },
            { value: "sound", label: "Good sound insulation" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Technology, Efficiency & Upkeep</h2>
        <CheckboxGroup
          label="Smart home technology"
          name="smartHome"
          values={form.smartHome}
          onChange={update}
          layout="grid"
          options={[
            { value: "none", label: "Keep it simple" },
            { value: "thermostat", label: "Smart thermostat" },
            { value: "locks", label: "Smart locks" },
            { value: "cameras", label: "Cameras / doorbell" },
            { value: "lighting", label: "Smart lighting" },
            { value: "wired", label: "Strong wiring / ethernet" },
          ]}
        />
        <CheckboxGroup
          label="Energy efficiency preferences"
          name="energy"
          values={form.energy}
          onChange={update}
          layout="grid"
          options={[
            { value: "solar", label: "Solar or solar-ready" },
            { value: "hvac", label: "Newer high-efficiency HVAC" },
            { value: "insulation", label: "Strong insulation" },
            { value: "windows", label: "Energy-efficient windows" },
            { value: "ev", label: "EV charger or 240V ready" },
            { value: "utility", label: "Lower utility bills matter" },
          ]}
        />
        <ChoiceGroup
          label="Maintenance level"
          name="maintenance"
          value={form.maintenance}
          onChange={update}
          options={[
            { value: "low", label: "Low-maintenance" },
            { value: "medium", label: "Some projects are fine" },
            { value: "high", label: "Happy to take on a project home" },
          ]}
        />
        <CheckboxGroup
          label="Lifestyle-specific requirements"
          name="specificNeeds"
          values={form.specificNeeds}
          onChange={update}
          layout="grid"
          options={[
            { value: "wfh", label: "Work from home most days" },
            { value: "kids", label: "Kids / play space" },
            { value: "multigen", label: "Multi-generational living" },
            { value: "hobbies", label: "Workshop / studio / music" },
            { value: "storage", label: "Serious storage / hobbies gear" },
            { value: "short-term", label: "May host guests often" },
          ]}
        />
        <NotesField
          label="Lifestyle notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Work hours, pets, hobbies, how you host, or anything that should shape the search."
        />
      </section>
    </ChecklistStep>
  );
}
